import React, { useState, useEffect, useRef } from 'react';
import {
    FaPlayCircle,
    FaTimes,
    FaChevronLeft,
    FaChevronRight,
    FaHeart,
    FaRegHeart,
    FaExpand
} from 'react-icons/fa';
import env from '../../env';

const ImageGallery = ({
    images = [],
    title = '',
    videoUrl = null,
    onVideoClick = null,
    status = null,
    statusBadgeClass = null,
    isFavorite = false,
    onToggleFavorite = null,
    baseUrl = env.BACKEND_STORAGE_URL,
    fallbackLogo = 'M',
    fallbackText = 'Maveshibids',
    className = ''
}) => {
    const [lightboxVisible, setLightboxVisible] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const thumbnailContainerRef = useRef(null);

    const openLightbox = (index) => {
        setSelectedImageIndex(index);
        setLightboxVisible(true);
    };

    const closeLightbox = () => {
        setLightboxVisible(false);
    };

    const nextImage = () => {
        if (images.length > 0) {
            setSelectedImageIndex((prev) => (prev + 1) % images.length);
        }
    };

    const prevImage = () => {
        if (images.length > 0) {
            setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);
        }
    };

    const goToImage = (index) => {
        setSelectedImageIndex(index);
    };

    // Scroll thumbnail into view when selected image changes
    useEffect(() => {
        if (thumbnailContainerRef.current) {
            const container = thumbnailContainerRef.current;
            const selectedThumb = container.querySelector(`[data-thumb-index="${selectedImageIndex}"]`);
            if (selectedThumb) {
                selectedThumb.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center'
                });
            }
        }
    }, [selectedImageIndex]);

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowRight') nextImage();
        if (e.key === 'ArrowLeft') prevImage();
        if (lightboxVisible && e.key === 'Escape') closeLightbox();
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [lightboxVisible, selectedImageIndex, images.length]);

    const handleVideoClick = () => {
        if (onVideoClick) {
            onVideoClick();
        }
    };

    const getImageUrl = (image) => {
        const imagePath = typeof image === 'string' ? image : image?.path;
        return imagePath?.startsWith('http') ? imagePath : `${baseUrl}/${imagePath}`;
    };

    // Fallback when no images
    if (!Array.isArray(images) || images.length === 0) {
        return (
            <div 
                className={`auction-image-fallback position-relative ${className}`} 
                style={{ 
                    minHeight: '500px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    backgroundColor: '#f8f9fa', 
                    padding: '20px',
                    borderRadius: '12px'
                }}
            >
                {videoUrl && (
                    <div className="position-absolute bottom-0 start-50 translate-middle-x mb-4" style={{ zIndex: 10 }}>
                        <button
                            className="btn btn-primary rounded-pill fw-semibold watch-video-btn-responsive"
                            onClick={handleVideoClick}
                            style={{
                                boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                                fontSize: '14px',
                                padding: '8px 20px'
                            }}
                        >
                            <FaPlayCircle className="me-2" />
                            <span className="d-none d-md-inline">Watch Video</span>
                            <span className="d-md-none">Watch</span>
                        </button>
                    </div>
                )}
                <div className="text-center">
                    <div className="auction-logo mb-3" style={{
                        width: '100px',
                        height: '100px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '48px',
                        fontWeight: 'bold',
                        margin: '0 auto',
                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
                    }}>
                        {fallbackLogo}
                    </div>
                    <p className="text-muted mb-0 fw-semibold" style={{ fontSize: '18px' }}>{fallbackText}</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <style>{`
                .image-gallery-slider {
                    position: relative;
                    overflow: hidden;
                    border-radius: 12px;
                    background-color: #000;
                }
                
                .slider-main-image-container {
                    position: relative;
                    width: 100%;
                    height: 450px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                }
                
                @media (max-width: 767px) {
                    .slider-main-image-container {
                        height: 300px;
                    }
                }
                
                @media (min-width: 768px) and (max-width: 991px) {
                    .slider-main-image-container {
                        height: 380px;
                    }
                }
                
                .slider-main-image {
                    max-width: 100%;
                    max-height: 100%;
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                    transition: opacity 0.3s ease;
                }
                
                .slider-nav-btn {
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    background-color: rgba(255, 255, 255, 0.9);
                    border: none;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                    z-index: 10;
                    color: #333;
                    font-size: 18px;
                }
                
                .slider-nav-btn:hover {
                    background-color: #fff;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
                    transform: translateY(-50%) scale(1.05);
                }
                
                .slider-nav-btn:active {
                    transform: translateY(-50%) scale(0.95);
                }
                
                .slider-nav-prev {
                    left: 12px;
                }
                
                .slider-nav-next {
                    right: 12px;
                }
                
                @media (max-width: 767px) {
                    .slider-nav-btn {
                        width: 36px;
                        height: 36px;
                        font-size: 14px;
                    }
                    
                    .slider-nav-prev {
                        left: 8px;
                    }
                    
                    .slider-nav-next {
                        right: 8px;
                    }
                }
                
                .slider-counter {
                    position: absolute;
                    bottom: 12px;
                    left: 50%;
                    transform: translateX(-50%);
                    background-color: rgba(0, 0, 0, 0.7);
                    color: white;
                    padding: 6px 14px;
                    border-radius: 20px;
                    font-size: 13px;
                    font-weight: 500;
                    z-index: 10;
                }
                
                .slider-fullscreen-btn {
                    position: absolute;
                    bottom: 12px;
                    right: 12px;
                    width: 40px;
                    height: 40px;
                    border-radius: 8px;
                    background-color: rgba(0, 0, 0, 0.6);
                    border: none;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    z-index: 10;
                    color: white;
                    font-size: 16px;
                }
                
                .slider-fullscreen-btn:hover {
                    background-color: rgba(0, 0, 0, 0.8);
                }
                
                .thumbnail-strip {
                    display: flex;
                    gap: 8px;
                    padding: 12px;
                    overflow-x: auto;
                    overflow-y: hidden;
                    background-color: #f8f9fa;
                    border-radius: 0 0 12px 12px;
                    scrollbar-width: thin;
                    scrollbar-color: #ccc #f8f9fa;
                }
                
                .thumbnail-strip::-webkit-scrollbar {
                    height: 6px;
                }
                
                .thumbnail-strip::-webkit-scrollbar-track {
                    background: #f8f9fa;
                    border-radius: 3px;
                }
                
                .thumbnail-strip::-webkit-scrollbar-thumb {
                    background: #ccc;
                    border-radius: 3px;
                }
                
                .thumbnail-strip::-webkit-scrollbar-thumb:hover {
                    background: #aaa;
                }
                
                .thumbnail-item {
                    flex-shrink: 0;
                    width: 70px;
                    height: 52px;
                    border-radius: 6px;
                    overflow: hidden;
                    cursor: pointer;
                    border: 2px solid transparent;
                    transition: all 0.2s ease;
                    opacity: 0.6;
                }
                
                .thumbnail-item:hover {
                    opacity: 0.9;
                    border-color: #667eea;
                }
                
                .thumbnail-item.active {
                    border-color: #667eea;
                    opacity: 1;
                    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
                }
                
                .thumbnail-item img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                
                @media (max-width: 767px) {
                    .thumbnail-strip {
                        padding: 8px;
                        gap: 6px;
                    }
                    
                    .thumbnail-item {
                        width: 56px;
                        height: 42px;
                    }
                }
                
                @media (min-width: 768px) {
                    .watch-video-btn-responsive {
                        font-size: 16px !important;
                        padding: 12px 40px !important;
                    }
                }
                
                /* Mobile Lightbox Full Screen */
                @media (max-width: 767px) {
                    .lightbox-container {
                        padding: 0 !important;
                    }
                    
                    .lightbox-header {
                        padding: 12px 16px !important;
                        min-height: 50px;
                        position: relative;
                        z-index: 10001;
                    }
                    
                    .lightbox-close-btn {
                        font-size: 28px !important;
                        min-width: 48px !important;
                        min-height: 48px !important;
                        background-color: rgba(255, 255, 255, 0.25) !important;
                        border-radius: 50% !important;
                        padding: 10px !important;
                        margin-left: 8px !important;
                    }
                    
                    .lightbox-close-btn:hover,
                    .lightbox-close-btn:active {
                        background-color: rgba(255, 255, 255, 0.4) !important;
                    }
                    
                    .lightbox-header-text {
                        font-size: 12px !important;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        max-width: calc(100vw - 120px);
                    }
                    
                    .lightbox-header-counter {
                        font-size: 12px !important;
                    }
                    
                    .lightbox-image-area {
                        padding: 0 !important;
                        height: calc(100vh - 50px) !important;
                        min-height: calc(100vh - 50px) !important;
                    }
                    
                    .lightbox-main-image {
                        max-height: 100vh !important;
                        max-width: 100vw !important;
                        width: 100% !important;
                        height: 100% !important;
                        object-fit: contain !important;
                        border-radius: 0 !important;
                    }
                    
                    .lightbox-nav-btn {
                        width: 48px !important;
                        height: 48px !important;
                        font-size: 24px !important;
                        margin-left: 10px !important;
                        margin-right: 10px !important;
                        padding: 8px !important;
                    }
                    
                    .lightbox-nav-prev {
                        left: 0 !important;
                    }
                    
                    .lightbox-nav-next {
                        right: 0 !important;
                    }
                }
            `}</style>
            
            <div className={`image-gallery-slider ${className}`}>
                {/* Main Image Slider */}
                <div className="slider-main-image-container">
                    {/* Main Image */}
                    <img
                        src={getImageUrl(images[selectedImageIndex])}
                        alt={`${title} - Image ${selectedImageIndex + 1}`}
                        className="slider-main-image"
                        onClick={() => openLightbox(selectedImageIndex)}
                        style={{ cursor: 'pointer' }}
                    />
                    
                    {/* Previous Button */}
                    {images.length > 1 && (
                        <button
                            className="slider-nav-btn slider-nav-prev"
                            onClick={(e) => {
                                e.stopPropagation();
                                prevImage();
                            }}
                            aria-label="Previous image"
                        >
                            <FaChevronLeft />
                        </button>
                    )}
                    
                    {/* Next Button */}
                    {images.length > 1 && (
                        <button
                            className="slider-nav-btn slider-nav-next"
                            onClick={(e) => {
                                e.stopPropagation();
                                nextImage();
                            }}
                            aria-label="Next image"
                        >
                            <FaChevronRight />
                        </button>
                    )}
                    
                    {/* Image Counter */}
                    {images.length > 1 && (
                        <div className="slider-counter">
                            {selectedImageIndex + 1} / {images.length}
                        </div>
                    )}
                    
                    {/* Fullscreen Button */}
                    <button
                        className="slider-fullscreen-btn"
                        onClick={() => openLightbox(selectedImageIndex)}
                        aria-label="View fullscreen"
                    >
                        <FaExpand />
                    </button>
                    
                    {/* Video Button */}
                    {videoUrl && (
                        <div className="position-absolute" style={{ bottom: '12px', left: '12px', zIndex: 10 }}>
                            <button
                                className="btn btn-primary rounded-pill fw-semibold"
                                onClick={handleVideoClick}
                                style={{
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                    fontSize: '13px',
                                    padding: '8px 16px'
                                }}
                            >
                                <FaPlayCircle className="me-2" />
                                Watch Video
                            </button>
                        </div>
                    )}
                    
                    {/* Status Badge */}
                    {status && (
                        <div className="position-absolute" style={{ top: '12px', right: '12px', zIndex: 10 }}>
                            <span className={`badge ${statusBadgeClass || 'bg-secondary'} px-3 py-2`}>
                                {status.toUpperCase()}
                            </span>
                        </div>
                    )}

                    {/* Favorite Button */}
                    {onToggleFavorite && (
                        <button
                            className="btn btn-light rounded-circle position-absolute"
                            onClick={onToggleFavorite}
                            style={{
                                top: '12px',
                                left: '12px',
                                width: '44px',
                                height: '44px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: isFavorite ? '0 0 8px rgba(255, 77, 79, 0.5)' : '0 2px 8px rgba(0,0,0,0.15)',
                                zIndex: 10,
                                border: 'none'
                            }}
                        >
                            {isFavorite ? (
                                <FaHeart style={{ color: '#ff4d4f', fontSize: '18px' }} />
                            ) : (
                                <FaRegHeart style={{ fontSize: '18px' }} />
                            )}
                        </button>
                    )}
                </div>
                
                {/* Thumbnail Strip */}
                {images.length > 1 && (
                    <div className="thumbnail-strip" ref={thumbnailContainerRef}>
                        {images.map((image, index) => (
                            <div
                                key={index}
                                data-thumb-index={index}
                                className={`thumbnail-item ${selectedImageIndex === index ? 'active' : ''}`}
                                onClick={() => goToImage(index)}
                            >
                                <img
                                    src={getImageUrl(image)}
                                    alt={`Thumbnail ${index + 1}`}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Lightbox Gallery */}
            {lightboxVisible && images.length > 0 && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 lightbox-container"
                    style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.95)',
                        zIndex: 9999,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                    onClick={closeLightbox}
                >
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-center p-3 text-white lightbox-header">
                        <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
                            <span className="lightbox-header-text" style={{ fontSize: '14px', opacity: 0.8 }}>
                                {(() => {
                                    const currentImage = images[selectedImageIndex];
                                    const imagePath = typeof currentImage === 'string' ? currentImage : currentImage?.path;
                                    return imagePath?.split('/').pop() || `Image ${selectedImageIndex + 1}`;
                                })()}
                            </span>
                            <span className="ms-3 lightbox-header-counter" style={{ fontSize: '14px', opacity: 0.6 }}>
                                ({selectedImageIndex + 1} / {images.length})
                            </span>
                        </div>
                        <button
                            className="btn btn-link text-white lightbox-close-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                closeLightbox();
                            }}
                            style={{ 
                                fontSize: '24px', 
                                textDecoration: 'none',
                                zIndex: 10000,
                                minWidth: '44px',
                                minHeight: '44px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}
                        >
                            <FaTimes />
                        </button>
                    </div>

                    {/* Main Image Area */}
                    <div
                        className="flex-grow-1 d-flex align-items-center justify-content-center position-relative lightbox-image-area"
                        style={{ padding: '20px', overflow: 'hidden' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Previous Button */}
                        {images.length > 1 && (
                            <button
                                className="btn btn-link text-white position-absolute start-0 top-50 translate-middle-y lightbox-nav-btn lightbox-nav-prev"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    prevImage();
                                }}
                                style={{
                                    fontSize: '32px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    borderRadius: '50%',
                                    width: '60px',
                                    height: '60px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginLeft: '20px',
                                    zIndex: 10
                                }}
                            >
                                <FaChevronLeft />
                            </button>
                        )}

                        {/* Main Image */}
                        <img
                            src={getImageUrl(images[selectedImageIndex])}
                            alt={`${title} - Image ${selectedImageIndex + 1}`}
                            className="img-fluid lightbox-main-image"
                            style={{
                                maxHeight: 'calc(100vh - 200px)',
                                maxWidth: 'calc(100% - 200px)',
                                objectFit: 'contain',
                                borderRadius: '8px'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        />

                        {/* Next Button */}
                        {images.length > 1 && (
                            <button
                                className="btn btn-link text-white position-absolute end-0 top-50 translate-middle-y lightbox-nav-btn lightbox-nav-next"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    nextImage();
                                }}
                                style={{
                                    fontSize: '32px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    borderRadius: '50%',
                                    width: '60px',
                                    height: '60px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: '20px',
                                    zIndex: 10
                                }}
                            >
                                <FaChevronRight />
                            </button>
                        )}
                    </div>

                    {/* Thumbnail Strip in Lightbox */}
                    {images.length > 1 && (
                        <div
                            className="d-none d-md-flex align-items-center justify-content-center p-3 lightbox-thumbnails"
                            style={{
                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                overflowX: 'auto',
                                overflowY: 'hidden'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="d-flex gap-2" style={{ minWidth: 'fit-content' }}>
                                {images.map((image, index) => (
                                    <div
                                        key={index}
                                        className="position-relative"
                                        style={{
                                            width: '100px',
                                            height: '75px',
                                            cursor: 'pointer',
                                            border: selectedImageIndex === index ? '3px solid #fff' : '3px solid transparent',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            flexShrink: 0
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedImageIndex(index);
                                        }}
                                    >
                                        <img
                                            src={getImageUrl(image)}
                                            alt={`Thumbnail ${index + 1}`}
                                            className="w-100 h-100"
                                            style={{
                                                objectFit: 'cover',
                                                opacity: selectedImageIndex === index ? 1 : 0.6,
                                                transition: 'opacity 0.3s ease'
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default ImageGallery;

